import React, { createContext, useContext, useReducer, useMemo, useState, useCallback, useEffect } from 'react';
import { StaticContext } from '@sm/webassets';
import packageReducer from '~shared/reducers/packageReducer';
import Grow2529Reducer from '~app/pages/Pricing/experiments/grow_2529_forms/reducer';
import Grow2792Reducer from '~app/pages/Pricing/experiments/grow_2792_pnp_refresh_v1/reducer';
import { combinedStates } from '~shared/state';

import { CombinedStates, RootPricingProviderStateType } from '~shared/types/pricing/state';
import { CombinedActions } from '~shared/types/pricing/actions';
import { PackageMap, PackagesProviderProps } from '~shared/types/pricing/package';
import { usePricingExperience } from './usePricingExperience';
import { PackageSkuCost, PricingPackage } from '~shared/types/pricing/pricingsvc';
import { PackageIds, SkuTypes } from '~shared/constants/pricing';
import ACTIONS from '~shared/actions';
import experimentFeatures from '~pricing/transformers/details/experimentFeatures';
import fakeFeatures from '~pricing/transformers/details/marketingFeatures';
import { usePricingExperiments } from './usePricingExperiments';
import { determineCombinedFeature, getFeatureValue } from '~app/pages/Pricing/transformers/details';
import { formatCostInLocaleCurrency } from '~app/pages/Pricing/lib/lib-helpers';
import { formatDetailsFeature } from '~pricing/lib/PackageFeatures';
import DETAILS_COPY from '~pricing/lib/PackageFeatures/details/copy';
import { DetailsPackageData } from '~app/pages/Pricing/transformers/details/types';

const detailsPackageData: DetailsPackageData = {};

export const PricingStateContext = createContext<RootPricingProviderStateType>({
  state: combinedStates,
  dispatch: () => {},
});

export function PricingStateProvider({
  packagesToDisplay,
  showEduDiscount,
  comparePlans,
  canonicalPath,
  subPath,
  children,
}: PackagesProviderProps): JSX.Element {
  const { packages: expPackages, discountInfo } = usePricingExperience();
  const [packagesForExperience, setPackagesForExperience] = useState<PricingPackage[]>(expPackages);
  const [packageIdToPackageMap, setPackageIdToPackageMap] = useState<PackageMap>({});
  const currentPath = canonicalPath ?? subPath;

  const {
    user,
    environment: { slLanguageLocale },
  } = useContext(StaticContext);

  const { uiExperiment, priceExperiment } = usePricingExperiments();

  const experience =
    (uiExperiment.hasAssignment && uiExperiment.treatmentName) ||
    (priceExperiment.hasAssignment && priceExperiment.treatmentName) ||
    '';

  // this will reformat the data from pricingsvc to match whether we are displaying
  // the educational discount or not. since the OG data contains _all_ the information,
  // we just need to fake what gets disseminated to the page components at this point
  useMemo(() => {
    const updatedPackageMap: PackageMap = {};
    const updatedPackages =
      expPackages?.map(pkg => {
        const newPackage = {
          ...pkg,

          // `comparisonSkuCosts` is the OG pricing data, so if this isn't EDU pricing,
          // we'll slip that in as the normal `skuCost`
          skuCost: showEduDiscount || !pkg.comparisonSkuCost?.length ? pkg.skuCost : pkg.comparisonSkuCost,

          // in normal view, don't send the comparison costs down
          comparisonSkuCost: showEduDiscount ? pkg.comparisonSkuCost : undefined,
        };
        updatedPackageMap[parseInt(newPackage.id, 10)] = newPackage;
        return newPackage;
      }) || [];

    setPackagesForExperience(updatedPackages);
    setPackageIdToPackageMap(updatedPackageMap);
  }, [showEduDiscount, expPackages, setPackagesForExperience, setPackageIdToPackageMap]);

  const getPackageById = useCallback(
    packageId => packageIdToPackageMap[parseInt(packageId, 10)],
    [packageIdToPackageMap]
  );

  const userPackage = useMemo<PricingPackage>(
    () => getPackageById(user.package || PackageIds.BASIC),
    [user, getPackageById]
  );

  const overageCost = useMemo<PackageSkuCost | undefined>(() => {
    // From the implementation of BILLING-18548, overage cost is package agnostic, so we'll
    // pull the overage SKU cost from team premier
    const teamPremier = getPackageById(PackageIds.TEAM_PREMIER_ANNUAL);
    return teamPremier?.skuCost.find(skuCost => skuCost.skuType === SkuTypes.ResponseOverage);
  }, [getPackageById]);

  const displayedPackages = useMemo(
    () => packagesToDisplay?.map(packageName => packageIdToPackageMap[PackageIds[packageName]]) || [],
    [packagesToDisplay, packageIdToPackageMap]
  );

  // Combine all reducers into one
  const rootReducer = useMemo(
    () =>
      ({ packages, grow2529, grow2792 }: CombinedStates, action: CombinedActions): CombinedStates => {
        return {
          packages: packageReducer(packages, action),
          grow2529: Grow2529Reducer(grow2529, action),
          grow2792: Grow2792Reducer(grow2792, action),
        };
      },
    []
  );

  const [state, dispatch] = useReducer(rootReducer, combinedStates);

  useEffect(() => {
    if (!state.packages?.displayPackages?.length) {
      dispatch({
        type: ACTIONS.UPDATE_PACKAGES,
        payload: { displayPackages: displayedPackages, allPackages: packagesForExperience },
      } as CombinedActions);

      dispatch({ type: ACTIONS.UPDATE_PACKAGES_TO_DISPLAY, payload: { packagesToDisplay } } as CombinedActions);
    }
  }, [dispatch, displayedPackages, packagesForExperience, packagesToDisplay, state.packages?.displayPackages?.length]);

  useEffect(() => {
    if (packagesToDisplay !== state.packages?.packagesToDisplayState) {
      // GROW-2629
      dispatch({ type: ACTIONS.FORMS_RESET_TOGGLE_SWITCH } as CombinedActions);
      // GROW=2792
      dispatch({ type: ACTIONS.PNP_REFRESH_RESET_ALL } as CombinedActions);
      // ALL
      dispatch({ type: ACTIONS.UPDATE_PACKAGES_TO_DISPLAY, payload: { packagesToDisplay } } as CombinedActions);
      dispatch({ type: ACTIONS.UPDATE_PACKAGES, payload: { displayPackages: displayedPackages } } as CombinedActions);
    }
  }, [dispatch, displayedPackages, packagesToDisplay, state.packages?.packagesToDisplayState]);

  const formattedOverageCost = useMemo(
    () => overageCost && formatCostInLocaleCurrency(overageCost, 2),
    [overageCost]
  ) as string;

  /**
   * Takes all packages and features for each and combines them with any experiment and other fake features not coming from pricingsvc.
   * Adds each feature to the detailsPackageData as an object with package keys and computed values.
   * This allows for a quicker lookup of details category values for each package without the need to run the transformers on every render when toggling
   * packages on the Details View
   *
   * Ex: {
   *  {
        BASIC: "View 25 responses per survey",
        name: "base_response_count",
        summary: "Number of responses",
        tooltip: "Each plan has a limited number of responses as indicated...",
        tooltipHeading: "Number of responses",
        ENT_PLATINUM: "Contact Us",
        STANDARD_MONTHLY: "1,000 responses per month**",
        STANDARD_ANNUAL: "2,500 responses per year**",
        ADVANTAGE_ANNUAL: "15,000 responses per year**",
        PREMIER_ANNUAL: "40,000 responses per year**",
        FORMS_MONTHLY: "200 responses per month**",
        FORMS_ANNUAL: "2,500 responses per year**",
        TEAM_ADVANTAGE_ANNUAL: "50,000 responses per year**",
        TEAM_PREMIER_ANNUAL: "100,000 responses per year**",
        ADVANTAGE_MONTHLY: "1,000 responses per month**"
      }
   * }
   */
  useMemo(
    () =>
      packagesForExperience?.forEach(pkg => {
        [...pkg.features, ...fakeFeatures, ...experimentFeatures].forEach(feature => {
          detailsPackageData[feature.name] = {
            ...detailsPackageData[feature.name],
            [pkg.packageName]:
              determineCombinedFeature(
                pkg.packageName,
                pkg.features,
                feature.name,
                slLanguageLocale,
                experience,
                currentPath
              ) ?? getFeatureValue(pkg.packageName, feature, slLanguageLocale, experience, currentPath),
            ...formatDetailsFeature(feature.name as keyof typeof DETAILS_COPY, { overageCost: formattedOverageCost }),
          };
        });
      }),
    [currentPath, experience, formattedOverageCost, packagesForExperience, slLanguageLocale]
  );

  const value = useMemo(() => {
    return {
      state: {
        ...state,
        packages: {
          ...state.packages,
          allPackages: packagesForExperience,
          comparePlans,
          detailsPackageData,
          displayPackages: state.packages.displayPackages.length ? state.packages.displayPackages : displayedPackages,
          discountInfo,
          displayPackageSet: state.packages.displayPackageSet.length
            ? state.packages.displayPackageSet
            : packagesToDisplay,
          packagesToDisplayState: packagesToDisplay,
          showEduDiscount,
          overageCost,
          userPackage,
          getPackageById,
        },
      },
      dispatch,
    };
  }, [
    comparePlans,
    discountInfo,
    displayedPackages,
    getPackageById,
    overageCost,
    packagesForExperience,
    packagesToDisplay,
    showEduDiscount,
    state,
    userPackage,
  ]);

  return <PricingStateContext.Provider value={value}>{children}</PricingStateContext.Provider>;
}

export const usePricingState = (): RootPricingProviderStateType => useContext(PricingStateContext);
